// Agent Response Form — structured proposal submission
import { useState } from "react";
import { Button, Card, Alert } from "@/components/shared";
import { api } from "@/services/api";
import { DemandLevel, type CreateResponsePayload, type ComparableSale } from "@/types";
import { TrendingUp, FileText } from "lucide-react";

interface AgentResponseFormProps {
  requestId: string;
  onSuccess: () => void;
}

export function AgentResponseForm({ requestId, onSuccess }: AgentResponseFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeValuation, setIncludeValuation] = useState(true);
  const [valMinDisplay, setValMinDisplay] = useState("");
  const [valMaxDisplay, setValMaxDisplay] = useState("");

  const [formData, setFormData] = useState({
    valuation_min: 0,
    valuation_max: 0,
    demand_level: DemandLevel.Medium,
    strategy_summary: "",
    commission_rate: undefined as number | undefined,
    comparable_sales: [] as ComparableSale[],
  });

  function formatRand(value: number): string {
    if (!value) return "";
    return value.toLocaleString("en-ZA");
  }

  function parseRand(text: string): number {
    return parseInt(text.replace(/[^0-9]/g, ""), 10) || 0;
  }

  function handleCurrencyChange(field: "valuation_min" | "valuation_max", raw: string) {
    const num = parseRand(raw);
    const display = num ? num.toLocaleString("en-ZA") : "";
    if (field === "valuation_min") setValMinDisplay(display);
    else setValMaxDisplay(display);
    updateField(field, num);
  }

  function updateField(field: string, value: any) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function updateComparable(index: number, field: keyof ComparableSale, value: any) {
    setFormData((prev) => {
      const sales = [...prev.comparable_sales];
      sales[index] = { ...sales[index], [field]: value };
      return { ...prev, comparable_sales: sales };
    });
  }

  function addComparable() {
    if (formData.comparable_sales.length < 5) {
      setFormData((prev) => ({
        ...prev,
        comparable_sales: [...prev.comparable_sales, { address: "", price: 0, date: "" }],
      }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const payload: CreateResponsePayload = {
        request_id: requestId,
        ...(includeValuation && formData.valuation_min ? { valuation_min: formData.valuation_min } : {}),
        ...(includeValuation && formData.valuation_max ? { valuation_max: formData.valuation_max } : {}),
        demand_level: formData.demand_level,
        comparable_sales: formData.comparable_sales.filter((s) => s.address && s.price),
        strategy_summary: formData.strategy_summary,
        commission_rate: formData.commission_rate,
      };
      await api.post("/responses", payload);
      onSuccess();
    } catch (e: any) {
      const details = e.body?.error?.details;
      if (details?.length) {
        setError(details.map((d: any) => `${d.field}: ${d.issue}`).join("\n"));
      } else {
        setError(e.message || "Failed to submit response");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert type="error"><span className="whitespace-pre-line">{error}</span></Alert>}

      {/* Valuation */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-sun" />
            Suburb Valuation Estimate
          </h4>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-white/50">{includeValuation ? "Included" : "Skip"}</span>
            <button
              type="button"
              role="switch"
              aria-checked={includeValuation}
              onClick={() => setIncludeValuation(!includeValuation)}
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${includeValuation ? "bg-teal-500" : "bg-white/20"}`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${includeValuation ? "translate-x-4" : "translate-x-0"}`} />
            </button>
          </label>
        </div>
        {includeValuation && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label text-xs">Min Value (ZAR)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">R</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={valMinDisplay}
                  onChange={(e) => handleCurrencyChange("valuation_min", e.target.value)}
                  placeholder="1,500,000"
                  className="input pl-7"
                />
              </div>
            </div>
            <div>
              <label className="label text-xs">Max Value (ZAR)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">R</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={valMaxDisplay}
                  onChange={(e) => handleCurrencyChange("valuation_max", e.target.value)}
                  placeholder="2,000,000"
                  className="input pl-7"
                />
              </div>
            </div>
          </div>
        )}
        {!includeValuation && (
          <p className="text-xs text-white/40 italic">No valuation will be included with this response.</p>
        )}
      </Card>

      {/* Demand Level */}
      <Card>
        <h4 className="text-sm font-semibold text-white mb-3">Buyer Demand Intensity</h4>
        <div className="flex gap-3">
          {Object.values(DemandLevel).map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => updateField("demand_level", level)}
              className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium capitalize transition-colors ${
                formData.demand_level === level
                  ? "border-gold bg-gold/10 text-sun"
                  : "border-white/15 text-white/60 hover:border-white/30"
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </Card>

      {/* Comparable Sales */}
      <Card>
        <h4 className="text-sm font-semibold text-white mb-3">Comparable Recent Sales (optional, up to 5)</h4>
        {formData.comparable_sales.length === 0 && (
          <p className="text-xs text-white/40 italic mb-3">Add comparable sales when you have them, or submit without them.</p>
        )}
        {formData.comparable_sales.map((sale, i) => (
          <div key={i} className="grid grid-cols-3 gap-3 mb-3">
            <input
              type="text"
              placeholder="Address/suburb"
              value={sale.address}
              onChange={(e) => updateComparable(i, "address", e.target.value)}
              className="input"
            />
            <input
              type="number"
              placeholder="Price (ZAR)"
              value={sale.price || ""}
              onChange={(e) => updateComparable(i, "price", Number(e.target.value))}
              className="input"
            />
            <input
              type="date"
              value={sale.date}
              onChange={(e) => updateComparable(i, "date", e.target.value)}
              className="input"
            />
          </div>
        ))}
        {formData.comparable_sales.length < 5 && (
          <Button type="button" variant="ghost" size="sm" onClick={addComparable}>
            + Add comparable sale
          </Button>
        )}
      </Card>

      {/* Strategy */}
      <Card>
        <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-sun" />
          Recommended Listing Strategy
        </h4>
        <textarea
          required
          value={formData.strategy_summary}
          onChange={(e) => updateField("strategy_summary", e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder="Describe your recommended approach for selling/renting this property..."
          className="input resize-none"
        />
        <div className="flex justify-between mt-2">
          <span className="text-xs text-white/40">{formData.strategy_summary.length}/1000</span>
          <div>
            <label className="text-xs text-white/50 mr-2">Commission (% excl. VAT)</label>
            <input
              type="number"
              step={0.25}
              min={0}
              max={10}
              value={formData.commission_rate || ""}
              onChange={(e) => updateField("commission_rate", e.target.value ? Number(e.target.value) : undefined)}
              className="input w-20"
              placeholder="e.g. 5.0"
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" loading={isSubmitting}>
          Submit Proposal
        </Button>
      </div>
    </form>
  );
}
